import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Key,
  Users,
  DollarSign,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  Clock
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboardPage = () => {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [expiringLicenses, setExpiringLicenses] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      const [dashRes, expiringRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers }),
        axios.get(`${API}/admin/expiring-licenses?days=30`, { headers })
      ]);
      setStats(dashRes.data);
      setExpiringLicenses(expiringRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Admin access required");
      } else {
        toast.error("Failed to load admin dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getDaysUntilExpiry = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const days = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return days;
  };

  const getExpiryBadge = (days) => {
    if (days <= 3) return "bg-red-500/10 text-red-500 border-red-500/20";
    if (days <= 7) return "bg-amber-500/10 text-amber-500 border-amber-500/20";
    return "bg-emerald-500/10 text-emerald-500 border-emerald-500/20";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
        <p className="text-lg font-medium">Admin Access Required</p>
        <p className="text-muted-foreground">You don't have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight flex items-center gap-3">
            <Key className="w-7 h-7 text-primary" />
            License Admin
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage licenses, affiliates, and payouts
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Active Licenses</p>
                <p className="text-2xl font-mono font-bold mt-1 text-emerald-500">{stats.licenses.active}</p>
              </div>
              <Key className="w-8 h-8 text-emerald-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Expiring (7 days)</p>
                <p className="text-2xl font-mono font-bold mt-1 text-amber-500">{stats.licenses.expiring_7_days}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Revenue</p>
                <p className="text-2xl font-mono font-bold mt-1">${stats.revenue.total.toLocaleString()}</p>
              </div>
              <DollarSign className="w-8 h-8 text-primary/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">This Month</p>
                <p className="text-2xl font-mono font-bold mt-1 text-primary">${stats.revenue.this_month.toLocaleString()}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-primary/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase">Total Licenses</p>
            <p className="text-xl font-mono font-bold mt-1">{stats.licenses.total}</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase">Expired</p>
            <p className="text-xl font-mono font-bold mt-1 text-muted-foreground">{stats.licenses.expired}</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase">Revoked</p>
            <p className="text-xl font-mono font-bold mt-1 text-red-500">{stats.licenses.revoked}</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase">Expiring (30 days)</p>
            <p className="text-xl font-mono font-bold mt-1">{stats.licenses.expiring_30_days}</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase">Affiliates</p>
            <p className="text-xl font-mono font-bold mt-1">{stats.affiliates.total}</p>
          </CardContent>
        </Card>
      </div>

      {/* Plan Distribution */}
      {stats.plan_distribution && stats.plan_distribution.length > 0 && (
        <Card className="bg-card/50 border-border">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Active Licenses by Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {stats.plan_distribution.map((p) => (
                <div key={p.plan} className="flex items-center gap-2">
                  <Badge variant="outline" className="uppercase">{p.plan}</Badge>
                  <span className="font-mono font-bold">{p.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expiring Licenses */}
      <Card className="bg-card/50 border-border" data-testid="expiring-licenses">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Licenses Expiring Soon
          </CardTitle>
          <Link to="/admin/licenses">
            <Button variant="ghost" size="sm">View All</Button>
          </Link>
        </CardHeader>
        <CardContent>
          {expiringLicenses.length > 0 ? (
            <div className="space-y-2">
              {expiringLicenses.slice(0, 10).map((license) => {
                const days = getDaysUntilExpiry(license.expires_at);
                return (
                  <div 
                    key={license.id}
                    className="flex items-center justify-between p-3 bg-background rounded border border-border"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{license.customer_name}</p>
                      <p className="text-xs text-muted-foreground">{license.customer_email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="uppercase">{license.plan}</Badge>
                      <Badge className={getExpiryBadge(days)}>
                        {days <= 0 ? "Expired" : `${days} days`}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center py-8 text-muted-foreground">
              No licenses expiring in the next 30 days
            </p>
          )}
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Link to="/admin/licenses">
          <Card className="bg-card/50 border-border hover:border-primary/50 transition-colors cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <Key className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium">Manage Licenses</p>
                <p className="text-xs text-muted-foreground">Create, extend, revoke</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/affiliates">
          <Card className="bg-card/50 border-border hover:border-primary/50 transition-colors cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <Users className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium">Manage Affiliates</p>
                <p className="text-xs text-muted-foreground">Partners & commissions</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/payouts">
          <Card className="bg-card/50 border-border hover:border-primary/50 transition-colors cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <DollarSign className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium">Affiliate Payouts</p>
                <p className="text-xs text-muted-foreground">Export for payment</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
